import type { VisitorType } from '../types/orgConfig';
import type { VideoExpiryPayload, VideoExpiryResponse } from '../types/videoExpiry';

// EN-4854: single source of truth for whether the safety-video step belongs in
// the flow. Used by every navigation site and by the progress bar so the routing
// and the step count can never disagree.
//
// videoSkip is only ever true when checkVideoExpiry explicitly returned
// isVideoExpired === false. Every other path — not yet checked, request failed,
// malformed response, window disabled — leaves it false and the video is shown,
// which is the safe direction.

// Folds in the checks the navigation sites used to repeat inline, so every site
// agrees on what "has a video" means. The blank check is the same as
// touchlessService.isNullUndefinedOrBlank (null, undefined, ''), kept local so this
// helper does not import the API service; the 'null' / 'undefined' strings were
// the extra checks most sites already made.
export const hasVideo = (videoUrl: string | null | undefined): boolean =>
  videoUrl != null && videoUrl !== '' && videoUrl !== 'null' && videoUrl !== 'undefined';

export const shouldShowVideo = (
  videoUrl: string | null | undefined,
  videoSkip: boolean | undefined | null,
): boolean => hasVideo(videoUrl) && videoSkip !== true;

// Decides whether this visitor may skip the video. Returns true only on an
// explicit isVideoExpired === false; a missing field must never read as "skip".
export const resolveVideoSkip = async (
  visitorType: Pick<VisitorType, 'videoUrl' | 'videoExpiryDays'> | undefined,
  payload: VideoExpiryPayload,
  check: (payload: VideoExpiryPayload) => Promise<VideoExpiryResponse>,
): Promise<boolean> => {
  // 0/null days means reuse is off for this visitor type (the video is shown
  // every visit), and no video means there is nothing to skip — either way there
  // is nothing to ask the backend. Mirrors the kiosk's videoExpiryDays <= 0 guard.
  const videoExpiryDays = Number(visitorType?.videoExpiryDays) || 0;
  if (!hasVideo(visitorType?.videoUrl) || videoExpiryDays <= 0) {
    return false;
  }

  try {
    const response = await check(payload);
    return response?.isVideoExpired === false;
  } catch (error) {
    // Fail safe: a failed check means the visitor watches the video as normal.
    console.error('Video expiry check failed:', error);
    return false;
  }
};
